import type {
  CollectionReference,
  PartialWithFieldValue,
  WriteBatch,
} from 'firebase/firestore'
import {
  collection,
  doc,
  orderBy,
  query,
  serverTimestamp,
  type Firestore,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import {
  collectionSnapshots,
  convertDocumentSnapshotToModel,
} from '../../firestore-mobx/stream'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import type { FirestoreSectionAssignmentConversation } from './schema'
import { schema, writeSchema } from './schema'
import type { StreamInterface } from 'tricklejs/dist/types'
import { SectionAssignmentConversation } from '../../models/SectionAssignmentConversation'
import { addDocWithError } from '../../firestore-mobx/fetch'

const converter: FirestoreDataConverter<FirestoreSectionAssignmentConversation> =
  {
    toFirestore: (
      data: PartialWithFieldValue<FirestoreSectionAssignmentConversation>
    ) => {
      writeSchema.partial().parse(data)
      return data
    },
    fromFirestore: (snapshot: QueryDocumentSnapshot) => {
      const data = snapshot.data({ serverTimestamps: 'estimate' })
      return schema.parse(data)
    },
  }

export const empty = Object.freeze(schema.parse({}))

const getColRef = (
  firestore: Firestore,
  { sectionId, assignmentId }: { sectionId: string; assignmentId: string }
): CollectionReference<FirestoreSectionAssignmentConversation> => {
  return collection(
    firestore,
    'section',
    sectionId,
    'assignment',
    assignmentId,
    'conversation'
  ).withConverter(converter)
}

export const getSectionAssignmentConversationsStream = (
  repository: FirebaseRepository,
  params: { sectionId: string; assignmentId: string }
): StreamInterface<SectionAssignmentConversation[]> => {
  const ref = getColRef(repository.firestore, params)

  const orderByCreatedAt = orderBy('createdAt', 'desc')
  const conversationsQuery = query(ref, orderByCreatedAt)

  return collectionSnapshots(conversationsQuery).map((snapshot) => {
    const models = snapshot.docs.map((doc) => {
      return convertDocumentSnapshotToModel(
        repository,
        doc,
        SectionAssignmentConversation
      )
    })
    return models
  })
}

export const createSectionAssignmentConversation = async (
  repository: FirebaseRepository,
  params: {
    sectionId: string
    assignmentId: string
    userId: string
  }
): Promise<string> => {
  const ref = getColRef(repository.firestore, {
    sectionId: params.sectionId,
    assignmentId: params.assignmentId,
  })

  const data = {
    sectionId: params.sectionId,
    assignmentId: params.assignmentId,
    userId: params.userId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }

  const docRef = await addDocWithError(ref, data, 'CreateDraftAssignmentError')
  return docRef.id
}

export const batchDeleteSectionAssignmentConversation = (
  repository: FirebaseRepository,
  params: {
    batch: WriteBatch
    sectionId: string
    assignmentId: string
    conversationId: string
  }
) => {
  const ref = getColRef(repository.firestore, {
    sectionId: params.sectionId,
    assignmentId: params.assignmentId,
  })

  const docRef = doc(ref, params.conversationId)
  return params.batch.delete(docRef)
}
